import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { favoritesApi, downloadsApi, authApi, notificationsApi, subscriptionsApi } from '../lib/api';
import { toast } from 'sonner';
import { 
  Heart, 
  Download, 
  FileText, 
  Trash2, 
  ArrowRight,
  Calendar,
  Building2,
  ShieldCheck,
  ShieldOff,
  Key,
  Copy,
  Check,
  Bell,
  Sparkles,
  Megaphone,
  RefreshCw,
  Mail,
  CreditCard,
  AlertTriangle,
  MessageSquarePlus,
  Rocket,
  Search,
  BarChart3,
  Target,
  Phone,
  Users,
  Palette,
  BookOpen,
  GraduationCap,
  Handshake,
} from 'lucide-react';
import FeedbackForm from '../components/FeedbackForm';
import QuickStartWizard from '../components/QuickStartWizard';
import ConsultingPopup from '../components/ConsultingPopup';

export default function Dashboard() {
  const { user, loading: authLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [favorites, setFavorites] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling2FA, setToggling2FA] = useState(false);
  const [recoveryKey, setRecoveryKey] = useState(null);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [grantStats, setGrantStats] = useState(null);
  const [watchlistCount, setWatchlistCount] = useState(0);
  const [templateCount, setTemplateCount] = useState(0);

  const API = process.env.REACT_APP_BACKEND_URL;

  const activeTab = searchParams.get('tab') || 'overview';

  // Show wizard for first-time users
  useEffect(() => {
    if (user && !localStorage.getItem('raiseroot_onboarded')) {
      setShowWizard(true);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        const [favRes, downRes, notifRes] = await Promise.all([
          favoritesApi.getAll(),
          downloadsApi.getHistory(),
          notificationsApi.getAll(),
        ]);
        setFavorites(favRes.data);
        setDownloads(downRes.data);
        setNotifications(notifRes.data.notifications || []);
        setUnreadCount(notifRes.data.unread_count || 0);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Fetch grant stats for journey progress
  useEffect(() => {
    const fetchJourneyData = async () => {
      if (!user) return;
      const token = localStorage.getItem('pitchpal_token');
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const [statsRes, watchRes, tmplRes] = await Promise.allSettled([
          fetch(`${API}/api/grants/stats/summary`, { headers }),
          fetch(`${API}/api/grants/watchlist`, { headers }),
          fetch(`${API}/api/grants/templates`, { headers }),
        ]);
        if (statsRes.status === 'fulfilled' && statsRes.value.ok) setGrantStats(await statsRes.value.json());
        if (watchRes.status === 'fulfilled' && watchRes.value.ok) { const d = await watchRes.value.json(); setWatchlistCount(Array.isArray(d) ? d.length : 0); }
        if (tmplRes.status === 'fulfilled' && tmplRes.value.ok) { const d = await tmplRes.value.json(); setTemplateCount(Array.isArray(d) ? d.length : 0); }
      } catch (e) { console.error(e); }
    };
    fetchJourneyData();
  }, [user]);

  // Compute smart next steps based on user state
  const getNextSteps = () => {
    const steps = [];
    const hasOrg = !!user.organization_name;
    const hasGrants = (grantStats?.total || 0) > 0;
    const hasWatchlist = watchlistCount > 0;
    const hasTemplates = templateCount > 0;
    const hasSubmitted = (grantStats?.by_status?.submitted || 0) > 0;
    const hasAwarded = (grantStats?.by_status?.awarded || 0) > 0;
    const upcomingDeadlines = grantStats?.upcoming_deadlines || 0;

    // Priority 1: Complete org profile
    if (hasOrg && !hasGrants && !hasWatchlist) {
      steps.push({
        title: 'Search for your first grants',
        desc: 'Use 26+ filters to find opportunities matched to your mission and location.',
        link: '/grants',
        icon: Search,
        iconBg: 'bg-amber-50',
        iconColor: 'text-amber-600',
        priority: 'high',
      });
    }

    // Priority 2: Build proposals
    if (!hasTemplates) {
      steps.push({
        title: 'Save your first writing template',
        desc: 'Upload a past LOI or proposal to reuse and adapt for future applications.',
        link: '/grants?tab=templates',
        icon: BookOpen,
        iconBg: 'bg-violet-50',
        iconColor: 'text-violet-600',
        priority: hasGrants ? 'high' : 'normal',
      });
    }

    // Upcoming deadlines
    if (upcomingDeadlines > 0) {
      steps.push({
        title: `${upcomingDeadlines} deadline${upcomingDeadlines > 1 ? 's' : ''} coming up`,
        desc: 'Review your calendar and make sure submissions are on track.',
        link: '/grants?tab=calendar',
        icon: Calendar,
        iconBg: 'bg-red-50',
        iconColor: 'text-red-600',
        priority: 'high',
      });
    }

    // Track grants
    if (hasWatchlist && !hasGrants) {
      steps.push({
        title: 'Start tracking a grant application',
        desc: 'Move a saved grant into your tracker to manage the full application process.',
        link: '/grants?tab=tracker',
        icon: BarChart3,
        iconBg: 'bg-blue-50',
        iconColor: 'text-blue-600',
        priority: 'high',
      });
    }

    // Promote your mission
    if (hasGrants) {
      steps.push({
        title: 'Create a campaign or flyer',
        desc: 'Use Design Studio to build materials that attract funders and supporters.',
        link: '/design-studio',
        icon: Palette,
        iconBg: 'bg-rose-50',
        iconColor: 'text-rose-600',
        priority: 'normal',
      });
    }

    // Build proposals with AI
    steps.push({
      title: 'Write a proposal or LOI',
      desc: 'Use the Proposal Builder to generate a draft based on your organization profile.',
      link: '/ai-generator',
      icon: FileText,
      iconBg: 'bg-primary-50',
      iconColor: 'text-primary',
      priority: !hasSubmitted ? 'high' : 'normal',
    });

    // Strategy call
    steps.push({
      title: 'Book a strategy session',
      desc: 'Get expert guidance on your next funding move — 30 min from $55.',
      link: '/consulting',
      icon: Phone,
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600',
      priority: 'normal',
    });

    // Learn
    steps.push({
      title: 'Explore the Training Library',
      desc: '14 expert articles on grant writing, donor cultivation, and nonprofit strategy.',
      link: '/community?tab=training',
      icon: GraduationCap,
      iconBg: 'bg-stone-100',
      iconColor: 'text-stone-600',
      priority: 'normal',
    });

    // Show top 3, prioritize high
    return steps.sort((a, b) => (a.priority === 'high' ? -1 : 1) - (b.priority === 'high' ? -1 : 1)).slice(0, 3);
  };

  const handleRemoveFavorite = async (templateId) => {
    try {
      await favoritesApi.remove(templateId);
      setFavorites(favorites.filter(f => f.template_id !== templateId));
      toast.success('Removed from favorites');
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast.error('Failed to remove from favorites');
    }
  };

  const handleTabChange = (value) => {
    setSearchParams({ tab: value });
  };

  const handleToggle2FA = async () => {
    setToggling2FA(true);
    try {
      const res = await authApi.toggle2FA();
      await refreshUser();
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Failed to update 2FA settings');
    } finally {
      setToggling2FA(false);
    }
  };

  const handleRegenerateKey = async () => {
    setGeneratingKey(true);
    try {
      const res = await authApi.regenerateRecoveryKey();
      setRecoveryKey(res.data.recovery_key);
      toast.success('New recovery key generated');
    } catch (error) {
      toast.error('Failed to generate recovery key');
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleCopyRecoveryKey = () => {
    navigator.clipboard.writeText(recoveryKey);
    setCopiedKey(true);
    toast.success('Recovery key copied');
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationsApi.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'feature': return <Sparkles className="w-4 h-4 text-primary" />;
      case 'update': return <RefreshCw className="w-4 h-4 text-blue-500" />;
      case 'announcement': return <Megaphone className="w-4 h-4 text-amber-500" />;
      default: return <Bell className="w-4 h-4 text-stone-400" />;
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await subscriptionsApi.cancel();
      await refreshUser();
      toast.success('Subscription cancelled. You are now on the Free plan.');
      setShowCancelDialog(false);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to cancel subscription';
      toast.error(msg);
    } finally {
      setCancelling(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
        <div className="w-8 h-8 spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="dashboard-page">
      <ConsultingPopup />
      {showWizard && (
        <QuickStartWizard
          onClose={() => setShowWizard(false)}
          onComplete={() => setShowWizard(false)}
        />
      )}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-1">
              Welcome back, {user.organization_name}
            </h1>
            <p className="text-sm text-[#5C6B67]">
              Here's what to focus on today to move your fundraising forward.
            </p>
          </div>
          <div className="flex gap-2 self-start">
            <Link to="/consulting">
              <Button variant="outline" size="sm" className="border-primary text-primary hover:bg-primary-50" data-testid="dashboard-strategy-btn">
                <Phone className="w-4 h-4 mr-1" /> Strategy Call
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={() => setShowWizard(true)} className="text-stone-600" data-testid="open-wizard-btn">
                <Rocket className="w-4 h-4 mr-1" /> Quick Start
            </Button>
          </div>
        </div>

        {/* ===== FUNDING JOURNEY PROGRESS ===== */}
        <div className="mb-8 bg-white border border-stone-200 rounded-2xl p-5" data-testid="journey-progress">
          <p className="text-xs text-[#5C6B67] font-medium uppercase tracking-wider mb-3">Your Funding Journey</p>
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: 'Profile', done: !!user.organization_name, link: '/organization', icon: Building2 },
              { label: 'Find Grants', done: grantStats?.total > 0 || watchlistCount > 0, link: '/grants', icon: Search },
              { label: 'Corporate Partners', done: false, link: '/partners', icon: Handshake },
              { label: 'Win Funding', done: grantStats?.by_status?.awarded > 0, link: '/grants?tab=tracker', icon: Target },
            ].map((step, i) => (
              <Link to={step.link} key={step.label} className="group">
                <div className={`text-center p-3 rounded-xl transition-all ${step.done ? 'bg-green-50 border border-green-200' : 'bg-[#FAFAFA] border border-stone-200 hover:border-primary/30'}`}>
                  <step.icon className={`w-5 h-5 mx-auto mb-1 ${step.done ? 'text-green-600' : 'text-stone-400 group-hover:text-primary'}`} />
                  <p className={`text-xs font-medium ${step.done ? 'text-green-700' : 'text-stone-500'}`}>{step.label}</p>
                  {step.done && <Check className="w-3 h-3 text-green-500 mx-auto mt-0.5" />}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* ===== NEXT STEPS CARDS ===== */}
        <div className="mb-8 space-y-3" data-testid="next-steps">
          <h2 className="font-heading font-bold text-lg text-[#0A0A0A]">Your Next Steps</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {getNextSteps().map((step, i) => (
              <Link to={step.link} key={i}>
                <Card className={`bg-white border hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer h-full ${step.priority === 'high' ? 'border-primary/30 bg-primary-50/30' : 'border-stone-200'}`} data-testid={`next-step-${i}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${step.iconBg}`}>
                        <step.icon className={`w-5 h-5 ${step.iconColor}`} />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-[#0A0A0A] mb-0.5">{step.title}</p>
                        <p className="text-xs text-[#5C6B67] leading-relaxed">{step.desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-stone-100">
                      <span className={`text-[10px] font-medium uppercase tracking-wider ${step.priority === 'high' ? 'text-primary' : 'text-stone-400'}`}>{step.priority === 'high' ? 'Recommended' : 'Next up'}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-stone-400" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* ===== PIPELINE SNAPSHOT ===== */}
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="font-heading text-base font-semibold text-stone-900">Pipeline Snapshot</h2>
          <button
            type="button"
            onClick={() => navigate('/grants?tab=tracker')}
            className="text-xs text-primary hover:underline"
            data-testid="pipeline-view-all-btn"
          >
            View tracker →
          </button>
        </div>
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Grants Tracked', val: grantStats?.total || 0, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Submitted', val: grantStats?.by_status?.submitted || 0, color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Awarded', val: grantStats?.by_status?.awarded || 0, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Amount Requested', val: `$${(grantStats?.total_requested || 0).toLocaleString()}`, color: 'text-primary', bg: 'bg-primary-50' },
          ].map(stat => (
            <Card key={stat.label} className="bg-white border-stone-200">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center`}>
                    <BarChart3 className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-[#0A0A0A]">{stat.val}</p>
                    <p className="text-xs text-[#5C6B67]">{stat.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="bg-stone-100 mb-8">
            <TabsTrigger value="overview" data-testid="tab-overview">Updates</TabsTrigger>
            <TabsTrigger value="favorites" data-testid="tab-favorites">Favorites</TabsTrigger>
            <TabsTrigger value="downloads" data-testid="tab-downloads">Downloads</TabsTrigger>
          </TabsList>

          {/* Overview Tab — now shows notifications, security, subscription */}
          <TabsContent value="overview">
            {/* Platform Updates */}
            {notifications.length > 0 && (
              <Card className="bg-white border-stone-200 mb-8" data-testid="notifications-card">
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <CardTitle className="text-lg font-semibold text-stone-900 flex items-center gap-2">
                    <Bell className="w-5 h-5 text-primary" />
                    Platform Updates
                    {unreadCount > 0 && (
                      <span className="bg-primary text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid="unread-count">
                        {unreadCount}
                      </span>
                    )}
                  </CardTitle>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-xs text-stone-500 hover:text-stone-700" data-testid="mark-all-read-btn">
                      Mark all read
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="space-y-2">
                  {notifications.slice(0, 5).map((n) => (
                    <div
                      key={n.id}
                      className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${n.read ? 'bg-white hover:bg-[#FAFAFA]' : 'bg-primary-50/50 hover:bg-primary-50'}`}
                      onClick={() => !n.read && handleMarkRead(n.id)}
                      data-testid={`notification-${n.id}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${n.read ? 'bg-stone-100' : 'bg-primary/10'}`}>
                        {getNotificationIcon(n.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium ${n.read ? 'text-stone-700' : 'text-stone-900'}`}>{n.title}</p>
                          {!n.read && <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-stone-500 mt-0.5 leading-relaxed">{n.message}</p>
                        <p className="text-[10px] text-stone-400 mt-1">
                          {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {/* Support Contact */}
                  <div className="pt-3 mt-2 border-t border-stone-100">
                    <p className="text-xs text-stone-500 flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5" />
                      Need help? Contact us at{' '}
                      <a href="mailto:info@strategicfundraisingconsulting.com" className="text-primary hover:underline font-medium">
                        info@strategicfundraisingconsulting.com
                      </a>
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Security Settings */}
            <Card className="bg-white border-stone-200 mb-8" data-testid="security-card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-stone-900 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  Security Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-[#FAFAFA] rounded-lg">
                  <div className="flex items-center gap-3">
                    {user.two_factor_enabled ? (
                      <ShieldCheck className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <ShieldOff className="w-5 h-5 text-stone-400 flex-shrink-0" />
                    )}
                    <div>
                      <p className="font-medium text-stone-900 text-sm">Two-Factor Authentication</p>
                      <p className="text-xs text-stone-500">
                        {user.two_factor_enabled 
                          ? 'A 6-digit code will be sent to your email on each login' 
                          : 'Add an extra layer of security to your account'}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant={user.two_factor_enabled ? "outline" : "default"}
                    size="sm"
                    onClick={handleToggle2FA}
                    disabled={toggling2FA}
                    data-testid="toggle-2fa-btn"
                    className={user.two_factor_enabled ? "border-red-200 text-red-600 hover:bg-red-50" : "bg-primary hover:bg-primary-800 text-white"}
                  >
                    {toggling2FA ? (
                      <div className="w-4 h-4 spinner" />
                    ) : user.two_factor_enabled ? 'Disable' : 'Enable'}
                  </Button>
                </div>

                {/* Recovery Key */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-[#FAFAFA] rounded-lg mt-3">
                  <div className="flex items-center gap-3">
                    <Key className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-stone-900 text-sm">Recovery Key</p>
                      <p className="text-xs text-stone-500">Generate a new key for password recovery</p>
                    </div>
                  </div>
                  {recoveryKey ? (
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono bg-stone-900 text-green-400 px-2 py-1 rounded max-w-[200px] truncate" data-testid="dashboard-recovery-key">
                        {recoveryKey}
                      </code>
                      <Button size="sm" variant="outline" onClick={handleCopyRecoveryKey} data-testid="copy-dashboard-key-btn">
                        {copiedKey ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRegenerateKey}
                      disabled={generatingKey}
                      data-testid="regenerate-key-btn"
                      className="border-amber-200 text-amber-700 hover:bg-amber-50"
                    >
                      {generatingKey ? <div className="w-4 h-4 spinner" /> : 'Generate New Key'}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Subscription Management */}
            {user.subscription_plan !== 'free' && (
              <Card className="bg-white border-stone-200 mb-8" data-testid="subscription-card">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-stone-900 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-primary" />
                    Subscription
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-[#FAFAFA] rounded-lg">
                    <div>
                      <p className="font-medium text-stone-900 text-sm">
                        Current Plan: <span className="text-primary capitalize">{user.subscription_plan.replace('_', ' ')}</span>
                        {user.is_lifetime_pro && <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Lifetime</span>}
                      </p>
                      <p className="text-xs text-stone-500 mt-1">
                        {user.is_lifetime_pro 
                          ? 'You have a lifetime subscription — no action needed'
                          : 'You can cancel anytime and keep access until the end of your billing period'}
                      </p>
                    </div>
                    {!user.is_lifetime_pro && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCancelDialog(true)}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        data-testid="cancel-subscription-btn"
                      >
                        Cancel Plan
                      </Button>
                    )}
                  </div>

                  {/* Cancel Confirmation Dialog */}
                  {showCancelDialog && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="cancel-dialog">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-medium text-sm text-red-800">Are you sure you want to cancel?</p>
                          <p className="text-xs text-red-600 mt-1">
                            You'll lose access to premium features and be downgraded to the Free plan immediately.
                          </p>
                          <div className="flex gap-2 mt-3">
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={handleCancelSubscription}
                              disabled={cancelling}
                              className="bg-red-600 hover:bg-red-700"
                              data-testid="confirm-cancel-btn"
                            >
                              {cancelling ? <div className="w-4 h-4 spinner" /> : 'Yes, Cancel'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setShowCancelDialog(false)}
                              data-testid="keep-subscription-btn"
                            >
                              Keep My Plan
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-3 text-center">
                    <p className="text-xs text-stone-500">
                      Need help with billing? Contact{' '}
                      <a href="mailto:info@strategicfundraisingconsulting.com" className="text-primary hover:underline">
                        info@strategicfundraisingconsulting.com
                      </a>
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Feedback Form */}
            {showFeedback && (
              <div className="mb-8" data-testid="feedback-section">
                <FeedbackForm onClose={() => setShowFeedback(false)} />
              </div>
            )}
          </TabsContent>

          {/* Favorites Tab */}
          <TabsContent value="favorites">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-20 bg-stone-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : favorites.length === 0 ? (
              <Card className="bg-white border-stone-200">
                <CardContent className="py-12 text-center">
                  <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-stone-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-stone-900 mb-2">No favorites yet</h3>
                  <p className="text-stone-600 mb-4">Save templates you love to find them quickly later.</p>
                  <Link to="/templates">
                    <Button className="bg-primary hover:bg-primary-800 text-white">
                      Browse Templates
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {favorites.map((favorite) => (
                  <Card key={favorite.id} className="bg-white border-stone-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <Link 
                          to={`/templates/${favorite.template_id}`}
                          className="flex items-center gap-3 flex-1 group"
                        >
                          <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                            <FileText className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-stone-900 group-hover:text-primary transition-colors">
                              {favorite.template_title}
                            </p>
                            <p className="text-sm text-stone-500 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Added {new Date(favorite.added_at).toLocaleDateString()}
                            </p>
                          </div>
                        </Link>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveFavorite(favorite.template_id)}
                          className="text-stone-400 hover:text-red-500 hover:bg-red-50"
                          data-testid={`remove-favorite-${favorite.template_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Downloads Tab */}
          <TabsContent value="downloads">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-20 bg-stone-100 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : downloads.length === 0 ? (
              <Card className="bg-white border-stone-200">
                <CardContent className="py-12 text-center">
                  <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Download className="w-8 h-8 text-stone-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-stone-900 mb-2">No downloads yet</h3>
                  <p className="text-stone-600 mb-4">Download templates to see them here.</p>
                  <Link to="/templates">
                    <Button className="bg-primary hover:bg-primary-800 text-white">
                      Browse Templates
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {downloads.map((download) => (
                  <Card key={download.id} className="bg-white border-stone-200">
                    <CardContent className="p-4">
                      <Link 
                        to={`/templates/${download.template_id}`}
                        className="flex items-center justify-between group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-secondary-50 rounded-lg flex items-center justify-center">
                            <Download className="w-6 h-6 text-secondary" />
                          </div>
                          <div>
                            <p className="font-medium text-stone-900 group-hover:text-primary transition-colors">
                              {download.template_title}
                            </p>
                            <p className="text-sm text-stone-500 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(download.downloaded_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-stone-100 rounded-full text-xs font-medium text-stone-600 uppercase">
                          {download.format}
                        </span>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
